/**
 * ProjectContext — the user's selected SCOPE (space?, project?), shared by
 * every surface (specs/changes/workspace-organization, scope-selector; extends
 * enterprise-project-organization's project-selector-surfaces).
 *
 * Selection is a VIEW FILTER, never an access decision: the visible lists
 * come from `GET /projects` / `GET /spaces` (grants-driven server-side), and
 * every scoped API still enforces spaces first.
 *
 * Scope invariants (the cascading Space → Project control):
 *  - selecting a project sets the space tier to that project's `spaceId`;
 *  - selecting a space clears the selected project when it doesn't belong to
 *    that space, and narrows the project list via `GET /projects?space=`;
 *  - "All spaces" clears both tiers (and both cookies);
 *  - the space tier is presentational only when the caller sees > 1 space —
 *    single-space tenants keep exactly the project-only selector.
 *
 * Resolution precedence (scope-selector spec):
 *   1. `/p/{slug}` path — handled by the ProjectPathResolver route, which
 *      writes both cookies and strips the prefix;
 *   2. `?project={idOrSlug}` query param (wins, rewrites the cookies);
 *   3. `?space={spaceId}` query param;
 *   4. the cross-subdomain `sdx_project` cookie (`{tenantSlug}:{projectId}`),
 *      which implies its space;
 *   5. the `sdx_space` cookie (`{tenantSlug}:{spaceId}`);
 *   6. none — "All". Exactly one visible project auto-selects.
 *
 * An unresolvable reference silently falls through — the user may have lost a
 * binding, and the 404 contract never names what they lost.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  PROJECT_COOKIE_NAME,
  PROJECT_QUERY_PARAM,
  SPACE_COOKIE_NAME,
  SPACE_QUERY_PARAM,
  type SelectorProject,
  type SelectorSpace,
} from '@systemdox/types';
import { projectsApi } from '@/services/projectsApi';
import { spacesApi } from '@/services/spacesApi';
import { useAuthContext } from '@/contexts/AuthContext';
import { getTenantSlug, rememberedTenantSlug } from '@/utils/subdomain';

export interface ProjectContextValue {
  /** Projects visible to the caller within the selected space (all spaces
   *  when none is selected). Empty ⇒ hide the project tier. */
  projects: SelectorProject[];
  /** Total visible pre-page — when > projects.length the selector must use
   *  server type-ahead (`search`) instead of local filtering (task 13). */
  total: number;
  /** Unarchived spaces visible to the caller. The selector shows the space
   *  tier only when there are ≥ 2 (single-space tenants see today's UI). */
  spaces: SelectorSpace[];
  loading: boolean;
  selected: SelectorProject | null;
  selectedSpace: SelectorSpace | null;
  /** Select a project (null = All projects); a project implies its space.
   *  Persists both cookies. */
  select: (project: SelectorProject | null) => void;
  /** Select a space (null = All spaces, which clears both tiers). Clears the
   *  selected project when it doesn't belong to the space. */
  selectSpace: (space: SelectorSpace | null) => void;
  /** Server-side name-prefix search (the selector's type-ahead at scale),
   *  scoped to the selected space. */
  search: (q: string) => Promise<SelectorProject[]>;
  refresh: () => Promise<void>;
}

const noop = async () => {};
const ProjectContext = createContext<ProjectContextValue>({
  projects: [],
  total: 0,
  spaces: [],
  loading: false,
  selected: null,
  selectedSpace: null,
  select: () => {},
  selectSpace: () => {},
  search: async () => [],
  refresh: noop,
});

function currentTenantSlug(): string | null {
  return getTenantSlug() ?? rememberedTenantSlug();
}

/** Write one cross-subdomain scope cookie (the rememberTenantSlug idiom):
 *  value `{tenantSlug}:{id}`, `Domain=.systemdox.com`, `SameSite=Lax`, 30 days. */
function writeScopeCookie(name: string, id: string | null): void {
  const slug = currentTenantSlug();
  if (!slug) return;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return;
  const value = id ? `${slug}:${id}` : '';
  const maxAge = id ? 30 * 24 * 3600 : 0;
  document.cookie =
    `${name}=${encodeURIComponent(value)}; domain=.systemdox.com; path=/; ` +
    `max-age=${maxAge}; SameSite=Lax; Secure`;
}

/** The remembered id for THIS tenant, if any — a stale cookie from another
 *  tenant must never select here. */
function readScopeCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]+)'));
  if (!match) return null;
  const [slug, id] = decodeURIComponent(match[1]).split(':', 2);
  const tenant = currentTenantSlug();
  return id && tenant && slug === tenant ? id : null;
}

/** Write the cross-subdomain project cookie. */
export function rememberProject(projectId: string | null): void {
  writeScopeCookie(PROJECT_COOKIE_NAME, projectId);
}

/** Write the cross-subdomain space cookie (scope-selector Phase C). */
export function rememberSpace(spaceId: string | null): void {
  writeScopeCookie(SPACE_COOKIE_NAME, spaceId);
}

/** The remembered projectId for THIS tenant, if any. */
export function rememberedProjectId(): string | null {
  return readScopeCookie(PROJECT_COOKIE_NAME);
}

/** The remembered spaceId for THIS tenant, if any. */
export function rememberedSpaceId(): string | null {
  return readScopeCookie(SPACE_COOKIE_NAME);
}

export interface InitialScopeRefs {
  queryProject: string | null;
  querySpace: string | null;
  cookieProject: string | null;
  cookieSpace: string | null;
}

/**
 * Initial scope resolution — pure, so the precedence chain is unit-testable:
 * `?project=` → `?space=` → project cookie (implies space) → space cookie →
 * auto-select single visible project. A project always implies its space;
 * space references resolve only against the visible (unarchived) space list.
 */
export function resolveInitialScope(
  projects: SelectorProject[],
  spaces: SelectorSpace[],
  refs: InitialScopeRefs,
): { projectId: string | null; spaceId: string | null } {
  const projectByRef = (ref: string | null) =>
    ref ? projects.find((p) => p.projectId === ref || p.slug === ref) ?? null : null;
  const spaceById = (id: string | null) =>
    id ? spaces.find((s) => s.spaceId === id) ?? null : null;
  const scopeOf = (p: SelectorProject) => ({ projectId: p.projectId, spaceId: p.spaceId });

  const fromQueryProject = projectByRef(refs.queryProject);
  if (fromQueryProject) return scopeOf(fromQueryProject);
  const fromQuerySpace = spaceById(refs.querySpace);
  if (fromQuerySpace) return { projectId: null, spaceId: fromQuerySpace.spaceId };
  const fromCookieProject = projectByRef(refs.cookieProject);
  if (fromCookieProject) return scopeOf(fromCookieProject);
  const fromCookieSpace = spaceById(refs.cookieSpace);
  if (fromCookieSpace) return { projectId: null, spaceId: fromCookieSpace.spaceId };
  if (projects.length === 1) return scopeOf(projects[0]);
  return { projectId: null, spaceId: null };
}

/** The scope after a project selection: a project implies its space; "All
 *  projects" (null) keeps the current space tier. Pure — the invariant. */
export function scopeAfterProjectSelect(
  project: SelectorProject | null,
  currentSpaceId: string | null,
): { projectId: string | null; spaceId: string | null } {
  return project
    ? { projectId: project.projectId, spaceId: project.spaceId }
    : { projectId: null, spaceId: currentSpaceId };
}

/** The scope after a space selection: "All spaces" (null) clears both tiers;
 *  a foreign space clears the selected project. Pure — the invariant. */
export function scopeAfterSpaceSelect(
  space: SelectorSpace | null,
  selectedProject: SelectorProject | null,
): { projectId: string | null; spaceId: string | null } {
  if (!space) return { projectId: null, spaceId: null };
  const keeps = selectedProject !== null && selectedProject.spaceId === space.spaceId;
  return { projectId: keeps ? selectedProject.projectId : null, spaceId: space.spaceId };
}

/** Unarchived spaces mapped to the frozen SelectorSpace shape; a listing
 *  failure degrades to "no space tier", never an error. */
async function fetchSelectorSpaces(): Promise<SelectorSpace[]> {
  try {
    const all = await spacesApi.listSpaces();
    return all
      .filter((s) => !s.archivedAt)
      .map(({ spaceId, name, isDefault }) => ({ spaceId, name, isDefault }));
  } catch {
    return [];
  }
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthContext();
  const [projects, setProjects] = useState<SelectorProject[]>([]);
  const [total, setTotal] = useState(0);
  const [spaces, setSpaces] = useState<SelectorSpace[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [resolvedInitial, setResolvedInitial] = useState(false);
  /** The space filter the current `projects` state was fetched under. */
  const fetchedForSpace = useRef<string | null>(null);

  // Initial load + resolution, together: precedence is applied to the freshly
  // fetched (unfiltered) lists, not to whatever state a previous render left.
  useEffect(() => {
    if (!isAuthenticated || resolvedInitial) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [{ projects: list, total: count }, spaceList] = await Promise.all([
          projectsApi.listProjects(),
          fetchSelectorSpaces(),
        ]);
        if (cancelled) return;
        setProjects(list);
        setTotal(count);
        setSpaces(spaceList);
        fetchedForSpace.current = null;
        const params = new URLSearchParams(window.location.search);
        const scope = resolveInitialScope(list, spaceList, {
          queryProject: params.get(PROJECT_QUERY_PARAM),
          querySpace: params.get(SPACE_QUERY_PARAM),
          cookieProject: rememberedProjectId(),
          cookieSpace: rememberedSpaceId(),
        });
        setSelectedId(scope.projectId);
        setSelectedSpaceId(scope.spaceId);
        if (scope.projectId) rememberProject(scope.projectId);
        if (scope.spaceId) rememberSpace(scope.spaceId);
      } catch {
        if (!cancelled) {
          setProjects([]); // fail soft: no selector, everything unscoped
          setTotal(0);
          setSpaces([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setResolvedInitial(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, resolvedInitial]);

  // The cascade: whenever the space tier changes, re-list projects narrowed
  // to that space (`GET /projects?space=` — the read plane, server-side).
  useEffect(() => {
    if (!resolvedInitial || !isAuthenticated) return;
    if (selectedSpaceId === fetchedForSpace.current) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { projects: list, total: count } = await projectsApi.listProjects(
          undefined,
          'read',
          selectedSpaceId ?? undefined,
        );
        if (cancelled) return;
        setProjects(list);
        setTotal(count);
        fetchedForSpace.current = selectedSpaceId;
      } catch {
        if (!cancelled) {
          setProjects([]);
          setTotal(0);
          fetchedForSpace.current = selectedSpaceId;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedInitial, isAuthenticated, selectedSpaceId]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [{ projects: list, total: count }, spaceList] = await Promise.all([
        projectsApi.listProjects(undefined, 'read', selectedSpaceId ?? undefined),
        fetchSelectorSpaces(),
      ]);
      setProjects(list);
      setTotal(count);
      setSpaces(spaceList);
      fetchedForSpace.current = selectedSpaceId;
    } catch {
      setProjects([]); // fail soft: no selector, everything unscoped
      setTotal(0);
      setSpaces([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, selectedSpaceId]);

  const select = useCallback(
    (project: SelectorProject | null) => {
      const next = scopeAfterProjectSelect(project, selectedSpaceId);
      setSelectedId(next.projectId);
      rememberProject(next.projectId);
      if (project) {
        // A project implies its space — sync the tier and both cookies.
        setSelectedSpaceId(next.spaceId);
        rememberSpace(next.spaceId);
      }
    },
    [selectedSpaceId],
  );

  const selectSpace = useCallback(
    (space: SelectorSpace | null) => {
      const current = projects.find((p) => p.projectId === selectedId) ?? null;
      const next = scopeAfterSpaceSelect(space, current);
      setSelectedSpaceId(next.spaceId);
      setSelectedId(next.projectId);
      if (!space) {
        // "All spaces" clears both tiers and both cookies.
        rememberSpace(null);
        rememberProject(null);
      } else {
        rememberSpace(next.spaceId);
        if (next.projectId === null && selectedId !== null) rememberProject(null);
      }
    },
    [projects, selectedId],
  );

  const search = useCallback(
    async (q: string) => {
      try {
        const { projects: matches } = await projectsApi.listProjects(
          q,
          'read',
          selectedSpaceId ?? undefined,
        );
        return matches;
      } catch {
        return [];
      }
    },
    [selectedSpaceId],
  );

  const value = useMemo<ProjectContextValue>(
    () => ({
      projects,
      total,
      spaces,
      loading,
      selected: projects.find((p) => p.projectId === selectedId) ?? null,
      selectedSpace: spaces.find((s) => s.spaceId === selectedSpaceId) ?? null,
      select,
      selectSpace,
      search,
      refresh,
    }),
    [projects, total, spaces, loading, selectedId, selectedSpaceId, select, selectSpace, search, refresh],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

/** Fail-soft: surfaces without a provider get an inert, selector-less context. */
// eslint-disable-next-line react-refresh/only-export-components
export function useProject(): ProjectContextValue {
  return useContext(ProjectContext);
}
